import { getAppImplementation, patchTask } from '@fno/service/api/tasks.js'
import { getUsersWithSchoolAccess, patchUser, createUser } from '@fno/service/api/users.js'
import { patchSchool } from '@fno/service/api/schools.js'
import { preFormMutator, preSaveMutator, getKeypath, preChangeMutator, nextHandler } from '@fno/route/tab/task/helpers.js'
import genericChange from '@fno/lib/form/generic-change.js'
import genericReset from '@fno/lib/form/generic-reset.js'
import genericSave from '@fno/lib/form/generic-save.js'
import schoolSchema from 'fanx-schema/resource/school'
import appImplementationSchema from 'fanx-schema/resource/taskAppImplementation'
import tabToolSchema from 'fanx-schema/resource/taskTabTool'
import userSchema from 'fanx-schema/resource/user'
import template from './GeneralInfo.svelte'
import transformApiResponseToFormObject from '@fno/lib/transform-api-response-to-form-object.js'
import setContextOriginal from '@fno/lib/form/set-context-original.js'
import deepEqual from 'deep-eql'
import dotProp from 'dot-prop'

import { getSchool } from '@fno/service/api/schools'
import { getTabTool } from '../../../../service/api/tasks'

const page = {
    school: {
        type: Object,
        schema: schoolSchema,
        patch: (school) => patchSchool({ school })
    },
    schoolOnboarding: {
        type: Object,
        schema: appImplementationSchema,
        patch: (task) => patchTask({ task })
    },
    users: {
        type: Array,
        schema: userSchema,
        patch: (user) => patchUser({ user }),
        post: (user) => createUser({ user })
    },
    clientOnboarding: {
        type: Object,
        schema: tabToolSchema,
        patch: (task) => patchTask({ task })
    }
}

export default (parent, stateRouter) => {
    const name = `${parent}.generalInfo`

    const customPreFormMutator = ({ context }) => {
        context.form.users.map(user => {
            const roles = dotProp.get(user, 'attributes.roles', []) || []

            user.attributes.$admin = roles.includes('admin')
            user.attributes.$notificationManager = roles.includes('notification-manager')
            user.attributes.$rewardsManager = roles.includes('rewards-manager')
            user.attributes.$attributesToPreserve = [ 'roles' ]

            return user
        })
        return preFormMutator({ context })
    }

    const customPreSaveMutator = ({ context, component }) => {
        context.form.users.map((user, index) => {
            let roles = []

            if (user.attributes.$admin) {
                roles.push('admin')
            }

            if (user.attributes.$notificationManager) {
                roles.push('notification-manager')
            }

            if (user.attributes.$rewardsManager) {
                roles.push('rewards-manager')
            }

            // if the newly constructed roles are different from the original roles,
            // it must be marked as a change
            if (!deepEqual(roles, dotProp.get(user, 'attributes.roles', []))) {
                user.attributes.roles = roles

                dotProp.set(context, `changes.users[${index}].attributes.roles`, roles)
            }

            // sessions are populated from  transformApiResponseToFormObject() but we don't
            // do anything with them and we don't want them....
            delete user.attributes.sessions

            return user
        })

        // because first and last name are not `required` in the schema, but required on this page
        // we must manually validate their presence in changes before the PATCH requests are sent
        context.alerts = context.alerts || []
        if (context.changes.users) {
            Object.keys(context.changes.users).forEach(index => {
                const changedUser = context.changes.users[index]
                const formUser = context.form.users[index]

                if (!formUser.attributes.firstname && !changedUser.attributes.firstname) {
                    context.alerts.push({
                        index,
                        level: 'error',
                        error: {
                            title: 'First name is required for users.',
                            detail: 'Validation Error'
                        }
                    })
                }

                if (!formUser.attributes.firstname && !changedUser.attributes.lastname) {
                    context.alerts.push({
                        index,
                        level: 'error',
                        error: {
                            title: 'Last name is required for users.',
                            detail: 'Validation Error'
                        }
                    })
                }
            })
        }

        const storeName = dotProp.get(context, 'form.schoolOnboarding.attributes.steps.INITIAL_DESIGN_REVIEW.appStoreName')
        dotProp.set(context, 'form.school.attributes.appStoreName', storeName)
        dotProp.set(context, 'changes.school.attributes.appStoreName', storeName)

        const iconName = dotProp.get(context, 'form.schoolOnboarding.attributes.steps.INITIAL_DESIGN_REVIEW.launchIconName')
        dotProp.set(context, 'form.school.attributes.appIconName', iconName)
        dotProp.set(context, 'changes.school.attributes.appIconName', iconName)

        if (context.alerts.length > 0) {
            component.$set({ context })
            throw new Error('Manual validation error on general info page')
        } else {
            return preSaveMutator({ context })
        }
    }

    stateRouter.addState({
        name,
        route: '/generalInfo',
        template,
        data: {
            title: 'General Info | T.A.B.'
        },
        resolve: async (ignore, { schoolId }) => {
            const [ users, { school }, schoolOnboarding, clientOnboarding ] = await Promise.all([
                getUsersWithSchoolAccess({ schoolId, roles: [ '-fnoexdev', '-fnoexsupport', '-prestosupport' ] }),
                getSchool({ schoolId, include: [ 'orgsData' ] }),
                getAppImplementation({ schoolId }),
                getTabTool({ schoolId })
            ])

            const context = {
                context: {
                    form: {
                        school: transformApiResponseToFormObject({
                            schema: schoolSchema,
                            data: school
                        }),
                        clientOnboarding: transformApiResponseToFormObject({
                            schema: tabToolSchema,
                            data: clientOnboarding
                        }),
                        schoolOnboarding: transformApiResponseToFormObject({
                            schema: appImplementationSchema,
                            data: schoolOnboarding
                        }),
                        users: users.map((user) =>
                            transformApiResponseToFormObject({ schema: userSchema, data: user })
                        )
                    }
                }
            }
            return setContextOriginal(context, customPreFormMutator)
        },
        activate: ({ domApi: component, parameters: { schoolId } }) => {
            const save = genericSave({
                component,
                page,
                preSaveMutator: customPreSaveMutator,
                preFormMutator: customPreFormMutator
            })
            component.$on('nextStepClicked', nextHandler({
                stateRouter,
                component,
                save,
                schoolId,
                stepId: 'GENERAL_INFO',
                nextId: (event) => event.detail.tier === 'tier3' ? 'affiliations' : 'venues'
            }))
            component.$on('save', save)
            component.$on('change', genericChange({ component, page, getKeypath, preChangeMutator }))
            component.$on('reset', genericReset({ component }))
        }
    })
}
